import { useReducer, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocalStorageSync } from "./useLocalStorageSync"; // Assuming this is in the same hooks folder
import { v4 as uuidv4 } from "uuid";
import { DEFAULT_TASK_DURATION } from "../config";
import { createTask, validateTask } from "../utils/taskUtils.js";

// Import sample data
import { SAMPLE_TASK_TEMPLATES } from "../sampleData"; // Adjust path as needed

// Helper function to recursively remove a tag from tasks and their children
const removeTagFromTaskTreeRecursively = (tasks, tagToRemove) => {
  return tasks.map((task) => {
    const newTags = task.tags ? task.tags.filter((t) => t !== tagToRemove) : [];
    const newChildren = task.children
      ? removeTagFromTaskTreeRecursively(task.children, tagToRemove)
      : [];
    return { ...task, tags: newTags, children: newChildren };
  });
};

// Helper function to recursively filter out completed tasks
const cleanUpCompletedTasksRecursively = (tasks) => {
  return tasks
    .filter((task) => !task.completed)
    .map((task) => ({
      ...task,
      children: task.children
        ? cleanUpCompletedTasksRecursively(task.children)
        : [],
    }));
};

// Modified to add new tasks to the top
const addTaskToTree = (tasks, parentId, task) => {
  if (!parentId) {
    return [task, ...tasks];
  }
  return tasks.map((t) => {
    if (t.id === parentId) {
      return { ...t, children: [task, ...(t.children || [])] };
    }
    if (t.children) {
      const updatedChildren = addTaskToTree(t.children, parentId, task);
      if (updatedChildren !== t.children) {
        return { ...t, children: updatedChildren };
      }
    }
    return t;
  });
};

const updateTaskInTree = (tasks, id, updates) => {
  return tasks.map((t) => {
    if (t.id === id) {
      const updatedTaskData = { ...t, ...updates };
      if (updates.hasOwnProperty("priority")) {
        updatedTaskData.priority =
          Number.isFinite(updates.priority) &&
          updates.priority >= 1 &&
          updates.priority <= 5
            ? updates.priority
            : updates.priority === null
            ? null
            : t.priority;
      }
      return updatedTaskData;
    }
    if (t.children) {
      return { ...t, children: updateTaskInTree(t.children, id, updates) };
    }
    return t;
  });
};

const deleteTaskFromTree = (tasks, id) => {
  if (!id) {
    return [];
  }
  return tasks
    .filter((t) => t.id !== id)
    .map((t) => ({
      ...t,
      children: t.children ? deleteTaskFromTree(t.children, id) : [],
    }));
};

const taskReducer = (state, action) => {
  switch (action.type) {
    case "SET_STATE":
      return {
        ...state,
        tasks: action.tasks || state.tasks,
        dailyTasks: action.dailyTasks || state.dailyTasks,
      };
    case "ADD_TASK":
      return {
        ...state,
        tasks: addTaskToTree(state.tasks, action.parentId, action.task),
      };
    case "UPDATE_TASK":
      return {
        ...state,
        tasks: updateTaskInTree(state.tasks, action.id, action.updates),
      };
    case "DELETE_TASK":
      return {
        ...state,
        tasks: deleteTaskFromTree(state.tasks, action.id),
      };
    case "ADD_DAILY_TASK":
      const newDailyTask = validateTask({ id: uuidv4(), ...action.task });
      return {
        ...state,
        dailyTasks: [newDailyTask, ...state.dailyTasks],
      };
    case "UPDATE_DAILY_TASK":
      return {
        ...state,
        dailyTasks: state.dailyTasks.map((task) =>
          task.id === action.id
            ? validateTask({ ...task, ...action.updates })
            : task
        ),
      };
    case "REMOVE_TAG_FROM_ALL_TASKS":
      return {
        ...state,
        tasks: removeTagFromTaskTreeRecursively(state.tasks, action.tag),
      };
    case "CLEAN_UP_COMPLETED_TASKS":
      return {
        ...state,
        tasks: cleanUpCompletedTasksRecursively(state.tasks),
      };
    default:
      return state;
  }
};

export function useTaskManager() {
  const { getItem, setItem } = useLocalStorageSync(["tasks", "dailyTasks"]);
  const [state, dispatch] = useReducer(taskReducer, {
    tasks: [],
    dailyTasks: [],
  });
  const hasLoadedTasksRef = useRef(false);

  useEffect(() => {
    if (hasLoadedTasksRef.current) return;
    const storedTasksRaw = getItem("tasks", null);
    const storedDailyTasks = getItem("dailyTasks", []);

    const processLoadedTask = (task) => {
      const validatedLoadedTask = validateTask(task);
      return {
        ...validatedLoadedTask,
        paused: task.completed ? task.paused : true,
        children: task.children ? task.children.map(processLoadedTask) : [],
      };
    };

    let initializedTasks;

    if (storedTasksRaw === null) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[useTaskManager] No 'tasks' key in localStorage. Loading sample tasks."
        );
      }
      const createSampleTaskRecursive = (template) => {
        const taskData = { ...template };
        delete taskData.children;

        if (template.name === "Set a Deadline & See Countdown") {
          taskData.deadline = new Date(
            Date.now() + 2 * 24 * 60 * 60 * 1000
          ).toISOString();
        }

        const newTask = createTask(taskData);

        if (template.children && template.children.length > 0) {
          newTask.children = template.children.map((childTemplate) =>
            createSampleTaskRecursive(childTemplate)
          );
        }
        return newTask;
      };
      initializedTasks = SAMPLE_TASK_TEMPLATES.map((template) =>
        createSampleTaskRecursive(template)
      );
    } else {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[useTaskManager] Found 'tasks' in localStorage. Processing stored tasks."
        );
      }
      initializedTasks = storedTasksRaw.map(processLoadedTask);
    }

    const initializedDailyTasks = storedDailyTasks.map(validateTask);

    if (process.env.NODE_ENV === "development") {
      console.log(
        "[useTaskManager] Dispatching SET_STATE with tasks:",
        initializedTasks
      );
      console.log(
        "[useTaskManager] Dispatching SET_STATE with dailyTasks:",
        initializedDailyTasks
      );
    }
    dispatch({
      type: "SET_STATE",
      tasks: initializedTasks,
      dailyTasks: initializedDailyTasks,
    });
    hasLoadedTasksRef.current = true;
  }, [getItem]);

  useEffect(() => {
    if (!hasLoadedTasksRef.current) return;
    if (!Array.isArray(state.tasks) || !Array.isArray(state.dailyTasks)) return;

    const tasksExistOrWereJustLoadedAsSamples =
      state.tasks.length > 0 || localStorage.getItem("tasks") === null;
    const dailyTasksExist = state.dailyTasks.length > 0;

    if (tasksExistOrWereJustLoadedAsSamples || dailyTasksExist) {
      if (localStorage.getItem("tasks") !== null || state.tasks.length > 0) {
        setItem("tasks", state.tasks);
      }
      if (
        localStorage.getItem("dailyTasks") !== null ||
        state.dailyTasks.length > 0
      ) {
        setItem("dailyTasks", state.dailyTasks);
      }
    } else if (
      localStorage.getItem("tasks") !== null ||
      localStorage.getItem("dailyTasks") !== null
    ) {
      setItem("tasks", state.tasks);
      setItem("dailyTasks", state.dailyTasks);
    }
  }, [state.tasks, state.dailyTasks, setItem]);

  useEffect(() => {
    if (!hasLoadedTasksRef.current) return;

    const today = new Date().toLocaleString("en-US", { weekday: "short" });
    state.dailyTasks.forEach((dailyTask) => {
      const isScheduledToday = dailyTask.daysOfWeek.includes(today);
      const taskAlreadyInMainList = state.tasks.find(
        (t) => t.dailyTaskId === dailyTask.id && !t.completed
      );
      const completedTaskInstanceExists = state.tasks.find(
        (t) => t.dailyTaskId === dailyTask.id && t.completed
      );

      if (isScheduledToday !== dailyTask.isActiveToday) {
        dispatch({
          type: "UPDATE_DAILY_TASK",
          id: dailyTask.id,
          updates: { isActiveToday: isScheduledToday },
        });
      }

      if (
        isScheduledToday &&
        !taskAlreadyInMainList &&
        !completedTaskInstanceExists
      ) {
        const taskDataForCreate = {
          name: dailyTask.name,
          duration: dailyTask.duration,
          tags: dailyTask.tags || [],
          notes: dailyTask.notes || "",
          isDaily: true,
          dailyTaskId: dailyTask.id,
          priority: dailyTask.priority,
        };
        const newTaskInstance = createTask(
          taskDataForCreate,
          dailyTask.duration
        );

        dispatch({
          type: "ADD_TASK",
          parentId: null,
          task: { ...newTaskInstance, isNew: true },
        });
      }
    });
  }, [state.dailyTasks, state.tasks]);

  const addTask = useCallback((parentId, taskData) => {
    const newTask = createTask(
      taskData,
      taskData.duration || DEFAULT_TASK_DURATION
    );
    dispatch({ type: "ADD_TASK", parentId, task: newTask });
  }, []);

  const updateTask = useCallback(
    (id, updates) => {
      dispatch({ type: "UPDATE_TASK", id, updates: updates });

      if (updates.completed) {
        const task = findTask(state.tasks, id);
        if (task?.isDaily && task.dailyTaskId) {
          const dailyTask = state.dailyTasks.find(
            (dt) => dt.id === task.dailyTaskId
          );
          if (dailyTask) {
            const todayDateStr = new Date().toISOString().split("T")[0];
            const lastCompletedDateStr = dailyTask.lastCompleted
              ? new Date(dailyTask.lastCompleted).toISOString().split("T")[0]
              : null;

            let newStreak = dailyTask.streak || 0;
            if (lastCompletedDateStr !== todayDateStr) {
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              const yesterdayDateStr = yesterday.toISOString().split("T")[0];

              if (lastCompletedDateStr === yesterdayDateStr) {
                newStreak += 1;
              } else {
                newStreak = 1;
              }
              dispatch({
                type: "UPDATE_DAILY_TASK",
                id: dailyTask.id,
                updates: {
                  lastCompleted: todayDateStr,
                  streak: newStreak,
                },
              });
            }
          }
        }
      }
    },
    [state.tasks, state.dailyTasks]
  );

  const deleteTask = useCallback((id) => {
    dispatch({ type: "DELETE_TASK", id });
  }, []);

  const addDailyTask = useCallback((task) => {
    const dailyTaskData = {
      ...task,
      daysOfWeek: Array.isArray(task.daysOfWeek) ? task.daysOfWeek : [],
    };
    const validatedDailyTask = validateTask(dailyTaskData);
    dispatch({ type: "ADD_DAILY_TASK", task: validatedDailyTask });
  }, []);

  const findTask = (tasksToSearch, id) => {
    for (const task of tasksToSearch) {
      if (task.id === id) return task;
      if (task.children) {
        const found = findTask(task.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const memoizedTasks = useMemo(() => state.tasks, [state.tasks]);
  const memoizedDailyTasks = useMemo(
    () => state.dailyTasks,
    [state.dailyTasks]
  );

  return {
    tasks: memoizedTasks,
    dailyTasks: memoizedDailyTasks,
    addTask,
    updateTask,
    deleteTask,
    addDailyTask,
    taskDispatch: dispatch,
  };
}
